import { WorkAttendanceType } from "../models/workAttendance";

export const getLocalISOString = (date: Date = new Date()): string => {
    const offset = date.getTimezoneOffset() * 60000; // offset in milliseconds
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().slice(0, 19);
};

export const getISODateFormat = (date: Date) => {
    if (!date) return;
    const yyyy: string = date.getFullYear().toString();
    const mm: string = (date.getMonth() + 1).toString().padStart(2, "0");
    const dd: string = date.getDate().toString().padStart(2, "0");
    // Date part of ISO standard (yyyy-MM-dd)
    return `${yyyy}-${mm}-${dd}`;
};

export const getISODateFormatForToday = () => getISODateFormat(new Date())!;

// This method should only be used for displaying dates.
// Do not use as date transport format when sending data to backend.
export const getDisplayDateFormat = (
    dateString?: string | Date,
    includeTime: boolean = false,
    localeFormat: string = "hr-HR"
): string => {
    if (!dateString) {
        return "";
    }
    const parsedDate = new Date(dateString);
    return includeTime
        ? parsedDate.toLocaleString(localeFormat)
        : parsedDate.toLocaleDateString(localeFormat);
};

export type TimeUnit = "year" | "day" | "hour" | "minute" | "second" | "millisecond";

const getDivider = (unit: Exclude<TimeUnit, "year">) => {
    let divider = 1;
    if (unit === "millisecond") return divider;
    divider *= 1000;
    if (unit === "second") return divider;
    divider *= 60;
    if (unit === "minute") return divider;
    divider *= 60;
    if (unit === "hour") return divider;

    return divider * 24;
};

export const getDateDiff = (start: Date, end: Date, unit: TimeUnit = "millisecond") => {
    if (unit === "year") {
        return end.getFullYear() - start.getFullYear();
    } else {
        let diff = end.getTime() - start.getTime();
        return Math.abs(diff / getDivider(unit));
    }
};

export const getDayOfWeek = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-us", {
        weekday: "long",
    });
};

export const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
};

const checkDates = (dates: Date[], selectedDates: Date[]) => {
    if (dates.length > selectedDates.length) {
        return dates[dates.length - 1];
    } else {
        for (let index = 0; index < selectedDates.length; index++) {
            const date = selectedDates[index];
            if (!dates.includes(date)) return date;
        }
    }
};

export const getDatesArray = (
    dates: Date | Date[] | undefined,
    attendanceType: WorkAttendanceType,
    disabledDates: Date | Date[],
    selectedDates: Date | Date[] | undefined
) => {
    if (attendanceType !== WorkAttendanceType.ParentalLeave) {
        return dates;
    } else {
        let parentalLeaveDates: Date[] = [];
        let date: Date | undefined = undefined;
        if (
            dates !== undefined &&
            Array.isArray(dates) &&
            dates.length > 2 &&
            selectedDates !== undefined &&
            Array.isArray(selectedDates)
        ) {
            date = checkDates(dates, selectedDates);
        }
        if (date !== undefined) return [date];
        const selectedDatesArray = Array.isArray(dates) ? dates : [dates];
        const disabledDatesArray = Array.isArray(disabledDates) ? disabledDates : [disabledDates];
        if (selectedDatesArray[1] && selectedDatesArray[0] && dates) {
            // here could be modulo operation used instead of copying everything but
            // typescript can't detect that %2 returns only 0 and 1
            let startDay: number;
            let startMonth: number;
            let startYear: number;
            let endDay: number;
            let endMonth: number;
            let endYear: number;
            if (selectedDatesArray[1].getTime() > selectedDatesArray[0].getTime()) {
                startDay = selectedDatesArray[0]?.getDate();
                startMonth = selectedDatesArray[0]?.getMonth();
                startYear = selectedDatesArray[0]?.getFullYear();
                endDay = selectedDatesArray[1]?.getDate();
                endMonth = selectedDatesArray[1]?.getMonth();
                endYear = selectedDatesArray[1]?.getFullYear();
            } else {
                startDay = selectedDatesArray[1]?.getDate();
                startMonth = selectedDatesArray[1]?.getMonth();
                startYear = selectedDatesArray[1]?.getFullYear();
                endDay = selectedDatesArray[0]?.getDate();
                endMonth = selectedDatesArray[0]?.getMonth();
                endYear = selectedDatesArray[0]?.getFullYear();
            }

            if (startYear === endYear) {
                parentalLeaveDates = getDatesArrayForOneYear(
                    disabledDatesArray,
                    startDay,
                    startMonth,
                    endDay,
                    endMonth,
                    startYear
                );
            } else {
                for (let year = startYear; year <= endYear; year++) {
                    if (year === startYear) {
                        parentalLeaveDates = parentalLeaveDates.concat(
                            getDatesArrayForOneYear(
                                disabledDatesArray,
                                startDay,
                                startMonth,
                                31,
                                12,
                                year
                            )
                        );
                    } else if (year === endYear) {
                        parentalLeaveDates = parentalLeaveDates.concat(
                            getDatesArrayForOneYear(
                                disabledDatesArray,
                                1,
                                1,
                                endDay,
                                endMonth,
                                year
                            )
                        );
                    } else {
                        parentalLeaveDates = parentalLeaveDates.concat(
                            getDatesArrayForOneYear(disabledDatesArray, 1, 1, 31, 12, year)
                        );
                    }
                }
            }
        } else {
            return dates;
        }
        return parentalLeaveDates;
    }
};

export const getDatesArrayForOneYear = (
    disabledDatesArray: Date[],
    startDay: number,
    startMonth: number,
    endDay: number,
    endMonth: number,
    year: number
) => {
    let parentalLeaveDates: Date[] = [];
    for (let j = startMonth; j <= endMonth; j++) {
        //month start with 0. this is why j is month + 1
        const daysInMonth = getDaysInMonth(year, j + 1);
        const endDayInCurrentMonth = endMonth === startMonth ? endDay : daysInMonth;
        for (let k = startDay; k <= endDayInCurrentMonth && j === startMonth; k++) {
            let selectedDate = new Date(year, j, k);
            if (
                !disabledDatesArray.includes(selectedDate) &&
                selectedDate.getDay() !== 0 &&
                selectedDate.getDay() !== 6
            ) {
                parentalLeaveDates.push(selectedDate);
            }
        }
        const endDayInOtherMonths = endMonth === j ? endDay : daysInMonth;
        for (let k = 1; k <= endDayInOtherMonths && j !== startMonth; k++) {
            let selectedDate = new Date(year, j, k);
            if (
                !disabledDatesArray.includes(selectedDate) &&
                selectedDate.getDay() !== 0 &&
                selectedDate.getDay() !== 6
            ) {
                parentalLeaveDates.push(selectedDate);
            }
        }
    }
    return parentalLeaveDates;
};

export const getDateInfo = (date?: string) => {
    let isToday = false,
        isBeforeToday = false,
        isAfterToday = false;
    let parsedDate: Date | null = null;

    if (date) {
        parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            parsedDate.setHours(0, 0, 0, 0);
            isToday = parsedDate.getTime() === today.getTime();
            isBeforeToday = parsedDate.getTime() < today.getTime();
            isAfterToday = parsedDate.getTime() > today.getTime();
        }
    }

    return {
        date,
        parsedDate,
        isToday,
        isBeforeToday,
        isAfterToday,
    };
};

export const getDateInfoForToday = () => getDateInfo(getISODateFormatForToday());
